<template lang="pug">
div
  div(v-if="loading" class="py-20 flex items-center justify-center")
    div
      //- div(class="mb-4 text-center text-sm text-gray-500") This seems to be a career page
      div(class="bg-white border rounded-full shadow-sm-soft p-2 flex items-center gap-2")
        img.spinner-loader(src="https://website-assets.atlan.com/spinner-blue.svg" alt="spinner" width="50" style="width:30px;height:auto")
        div Hold on, Loading Career Info...
  div(v-else-if="error" class="py-20 flex items-center justify-center")
    div(class="flex flex-col gap-4 justify-center text-center")
      div(class="font-gilroy heading-3 font-semibold") Error finding listing 
      a(class="btn btn-primary btn-sm" href="/careers") Back to Careers
  div(v-else-if="job")
    CareerProfileHeader(:job="job.props.job")
    JobDescription( :job="job.props.job" :formattedJob="job.props.formattedJob")
    CareerTestimonialPanel(:config="firstTestmonialSide")
    .py-6.bg-white
      .container.hidden.p-0(class="md:block")
        .flex.justify-center
          .p-0#captains-ball
          .flex.flex-col.justify-between.mx-2
            #himanshu-tot
            #library
          .p-0#cake
        .relative
    div(class="container py-10 lg:px-40")
      CarouselSliderBlock(v-bind="bigTestimonial")
    CareerTestimonialPanel(:config="thirdTestmonialSide")
    JobApplicationForm(:jdDetails="job.props.job")
    //- pre(class="text-xs") {{ job }}
</template>

<script lang="ts" setup>
import { ref, onMounted, defineProps } from "vue";
import leverApi from "@utils/api/career";

import CareerProfileHeader from "@components/common-panels/headers/CareerProfileHeader.vue";
import JobDescription from "@components/common-panels/content/JobDescription.vue";
import JobApplicationForm from "@components/common-panels/forms/JobApplicationForm.vue";
import CareerTestimonialPanel from "@components/common-panels/carousels/CareerTestimonialPanel.vue";
import CarouselSliderBlock from "@components/common-blocks/carousels/CarouselSliderBlock.vue";

const props = defineProps({
  loading: {
    type: Boolean,
    defaault: false,
  },
  error: {
    type: [Object, null],
    defaault: null,
  },
  job: {
    type: [Object, null],
    default: null,
  },
});

const firstTestmonialSide = {
  title: "Shape the future of our products",
  subtitle:
    "As part of the Business Team, you play a pivotal role. You are the voice of our users and inform all the important decisions we make. You will work closely with our users and prospects to understand their pain points and feed this information into our product roadmap.",
  slides: [
    {
      body: `<p data-v-f6481eca="" class="mb-3 font-size-md">Not only have I been able to understand how the Product Team approaches creating a product, but I’ve also been able to share our customers’ feedback with the team, which directly influences product development. <b>Here, your input can shape everything from the name of a product to the features it needs to have!</b></p>`,
      picture: "https://website-assets.atlan.com/img/team/kritika-small.webp",
      name: "Kritika Oberoi",
      role: "Founder's Staff",
    },
    {
      body: `<p data-v-f6481eca="" class="mb-3 font-size-md"><b>I'm the eyes and ears for the product team and give them a clear picture of our users’ feedback, their daily workflows, critical decisions, motivations and more.</b> This helps us build a product that truly solves the problems faced by every human of data that we work with!</p>`,
      picture: "https://website-assets.atlan.com/img/team/aishwarya-small.webp",
      name: "Aishwarya Bedekar",
      role: "Partnerships Manager",
    },
  ],
};
const bigTestimonial = {
  navType: true,
  slideClass: "p-6 lg:p-10 text-gray-700",
  bodyClass: "text-lg lg:text-xl",
  slides: [
    {
      body: `I've been astounded thus far with my experience at Atlan. Everyone is an A+ player, works hard, and believes in the common mission. What I love most is the reflective culture we have. Everyone is self-aware and highlights challenges as opposed to hiding them. Over-communication is paramount, even more so in a remote culture, and I think we do a great job of it.`,
      picture: "https://website-assets.atlan.com/img/team/andrew.webp",
      name: "Andrew Ermogenous",
      role: "Partnerships Manager",
    },
    {
      body: `A relentless focus on our core values is at the heart of everything that we do at Atlan. This helps build a strong culture in which professional growth is entwined with business growth, where everyone is committed to constant improvement. I have never met a single person at Atlan who just wants to leave things as they are — we are here to learn, get our hands dirty and push our boundaries!`,
      picture: "https://website-assets.atlan.com/img/team/mathura.webp",
      name: "Mathura Vaidyanathan",
      role: "Bangalore, India",
    },
    {
      body: `My first week here had me hit the ground running with speaking to customers, onboarding them, and taking complete ownership of their experience. In my role, I get to collaborate with almost all functions in Atlan and this excellent support system enables me to drive a great customer experience. Being a primary interface between customers and the product team and playing a pivotal role in the growth of the product is both rewarding and fulfilling!`,
      picture: "https://website-assets.atlan.com/img/team/ashwin.webp",
      name: "Ashwin Murali",
      role: "Bangalore, India",
    },
    {
      body: `From day one, I have been under GREAT mentors who worked hard to upgrade my knowledge and skills. I came from a different work background and yet they were patient with me, while at the same time they recognized my achievements. The people here are so motivated and dedicated, it's contagious! I've never worked for a company that's more welcoming and nurturing as Atlan.`,
      picture: "https://website-assets.atlan.com/img/team/isabel.webp",
      name: "Ma. Isabel Yonzon-Atienza",
      role: "Manila, Philippines",
    },
  ],
};
const thirdTestmonialSide = {
  title: "Ownership and autonomy from day one",
  subtitle:
    "At Atlan, business is all about putting the user first and addressing their needs. So the way your set your goals will be informed by the unique challenges that you need to solve. That’s why you have complete ownership of your projects—you set the priorities, decide on the experiments, and the best way to reach your goals.",
  slides: [
    {
      body: `<p data-v-f6481eca="" class="mb-3 font-size-md">Working at Atlan has been one for the books. <b>Waking up every day to the challenge of building the Africa business pipeline has forced me to grow both personally and professionally more ways than I ever imagined.</b> I have naturally grown to approach and tackle challenges differently as well as selectively choose my priorities to maximize my energy.</p>`,
      picture: "https://website-assets.atlan.com/img/team/quinta-small.webp",
      name: "Quinta Onditi",
      role: "Consultant",
    },
    {
      body: `<p data-v-f6481eca="" class="mb-3 font-size-md"><b>At Atlan, I’ve always been encouraged to take full ownership of my projects from the first day itself.</b> It is Atlan that I’ve got the opportunity to try various experiments to take our product across geographies and industries. Where else would I get so much freedom to experiment and manage my work.</p>`,
      picture: "https://website-assets.atlan.com/img/team/ujwal-small.webp",
      name: "Ujwal Chaudhari",
      role: "Sales Executive",
    },
  ],
};
</script>

<style>
#captains-ball {
  background-image: url("https://website-assets.atlan.com/img/culture/captains-ball.webp");
  background-size: cover;
  min-height: 450px;
  background-position: center;
  width: 400px;
}

#cake {
  background-image: url("https://website-assets.atlan.com/img/culture/cake.webp");
  background-size: cover;
  min-height: 450px;
  background-position: center;
  width: 400px;
}

#himanshu-tot {
  background-image: url("https://website-assets.atlan.com/img/culture/himanshu-talk.webp");
  background-size: cover;
  min-height: 220px;
  background-position: center;
  max-height: 220px;
  width: 300px;
}

#library {
  background-image: url("https://website-assets.atlan.com/img/culture/generic-atlan.webp");
  background-size: cover;
  min-height: 220px;
  background-position: center;
  max-height: 220px;
  min-width: 300px;
  max-width: 300px;
}
</style>
